import '../promo-code/promo-code';
import '../estimated-tax/estimated-tax';
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import {
  getCart,
  getCartStatus,
  getProductCurrencyCode,
  getProductsFromCart,
  isB2bK12Storefront,
} from './shop-cart/store/shop-cart.selectors';
import { ShopCart } from './shop-cart/interfaces/shop-cart.interface';
import { nothing } from 'lit-html';
import {
  getDigitalHandlingFeesAmount,
  getFreightCalculationStatus,
  getSelectedShippingTotal,
  getTaxCalculationStatus,
  getTotalPrice,
  getTotalTax,
  isCheckoutInfoSubmitted,
  getSelectedShippingMethod,
  getTotalBeforeDiscount,
} from '../checkout/shop-checkout';
import { BaseComponent } from '../../features/base-component/base-component';
import { ActionStatus } from '../../common/constants/action-status';
import { booleanConverter } from '../../common/converters';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import { isSuccessUser } from '../user-profile/user-profile.selectors';
import {
  addAnalyticsCheckoutFlowMetadata,
  getPriceWithCurrency,
  hasBundleDiscount,
  hasOnlyFreeTrialProducts,
  hasPromoCodeDiscount,
} from '../../common/utils/cart-util';
import { isTunnelPurchaseFlow } from '../../common/utils/util';
import { MessageTemplateId, loading, smallSpinner } from '../../common/constants/api-constants';
import emit from '../../common/events';
import { isGuestCheckoutPage } from '../../common/environment';
import { getShopCart } from './shop-cart/thunks/get-shop-cart.thunk';
import { isTaxCalculatedOnSchoolAddress } from '../estimated-tax/utils/tax.utils';
import { Modal } from '../modal/modal';
import { MessageType } from '../alert/global-message.interface';
import { getFreeTrialProduct } from '../free-trial/utils/free-trial.util';

@customElement('mhecomm-cart-summary')
export class CartSummary extends connect(store)(BaseComponent) {
  @query('button.btn-close')
  protected closeButton: HTMLButtonElement | undefined;

  /**
   * Which store to show a cart for. If not set it will show the national cart.
   */
  @property()
  storeCode?: string;

  @state()
  private isLoggedInUser = false;

  @state()
  private quantity!: number;

  @state()
  private isShippable?: boolean;

  @state()
  private total?: number;

  @state()
  private cartSubtotal?: number;

  @state()
  private totalBeforeDiscount?: number;

  @state()
  private bundleDiscount?: number;

  @state()
  private status = ActionStatus.idle;

  @state()
  private shippingTotal?: number;

  @state()
  private isShippingMethodSelected = false;

  @state()
  private isShippingLoading = false;

  @state()
  private totalTax?: number;

  @state()
  private isTaxLoading = false;

  @state()
  private digitalHandlingFees?: number;

  @state()
  private finalSummary = false;

  @state()
  private b2bStoretype = false;

  @state()
  private currencyCode;

  @state()
  private exemptionModal;

  @property({ converter: booleanConverter })
  checkoutPreview = false;

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    const cart = getCart(store.getState() as StoreState);
    if (!cart) {
      store.dispatch(getShopCart({ store: this.storeCode }));
    } else {
      this.updateCartSummary(cart!, store.getState() as StoreState);
    }
  }

  stateChanged(state: StoreState) {
    const cart = getCart(state);
    this.updateCartSummary(cart!, state);
    this.isTaxLoading = getTaxCalculationStatus(state) === ActionStatus.loading;
    this.isShippingLoading = getFreightCalculationStatus(state) === ActionStatus.loading;
  }

  updateCartSummary(cart: ShopCart, state: StoreState) {
    const count = cart?.productCount;
    this.isLoggedInUser = isSuccessUser(state);
    this.status = getCartStatus(state);
    this.isShippable = cart?.shippable;
    this.quantity = count ? count : 0;
    this.cartSubtotal = this.getSubTotal(cart);
    this.bundleDiscount = cart?.bundleDiscount;

    this.b2bStoretype = isB2bK12Storefront(state);
    this.shippingTotal = getSelectedShippingTotal(state);
    this.isShippingMethodSelected = !!getSelectedShippingMethod(state);
    this.totalTax = getTotalTax(state);
    this.digitalHandlingFees = getDigitalHandlingFeesAmount(state);
    this.finalSummary = isCheckoutInfoSubmitted(state);
    this.totalBeforeDiscount = getTotalBeforeDiscount(state);
    this.total = getTotalPrice(state);
    this.currencyCode = getProductCurrencyCode(state);
  }

  getSubTotal(cart: ShopCart) {
    return cart?.subTotal;
  }

  openModal() {
    this.exemptionModal.open();
    emit({
      element: this,
      name: 'click',
      detail: addAnalyticsCheckoutFlowMetadata({
        eventName: 'tax-exemption-link-click-show',
        label: 'Tax (exemptions)',
      }),
    });
  }

  renderComp() {
    const freeTrialProduct = getFreeTrialProduct();
    if (this.quantity > 0) {
      return html`
        ${this.headerHtmlTmpl()}
        <hr aria-hidden="true" />
        <div class="d-flex flex-row justify-content-between my-3" aria-live="polite" aria-atomic="true">
          ${isTunnelPurchaseFlow()
            ? html` <span> Subtotal:</span> `
            : html` <span> Cart Subtotal (${this.quantity} ${this.quantity > 1 ? html`items` : html`item`}):</span> `}
          <span class="fw-bold fs-large" id="cart-subtotal-value"
            >${getPriceWithCurrency(this.currencyCode, this.cartSubtotal!)}</span
          >
        </div>
        ${this.bundleDiscount ? this.bundleDiscountInfo(): nothing}
        ${this.shouldShowEstimatedTax() ? this.estimateTaxHtmlTmpl() : nothing}
        ${this.digitalHandlingFees
          ? html` <div
              part="service-fee"
              class="d-flex flex-row justify-content-between my-3"
              aria-live="polite"
              aria-atomic="true"
            >
              <span>Service Fee:</span>
              <span class="fw-bold fs-large" id="service-fee-value">
                ${this.isTaxLoading
                  ? smallSpinner()
                  : html`${getPriceWithCurrency(this.currencyCode, this.digitalHandlingFees!)}`}
              </span>
            </div>`
          : nothing}
        ${this.isShippable
          ? html` <div class="d-flex justify-content-between my-3" aria-live="polite" aria-atomic="true">
              <span>Shipping:</span>
              ${this.isShippingMethodSelected
                ? html`<span class="fw-bold fs-large" id="shipping-total-value"
                    >${this.isShippingLoading
                      ? smallSpinner()
                      : html`${getPriceWithCurrency(this.currencyCode, this.shippingTotal!)}`}</span
                  >`
                : html`<span class="float-end fs-small">shipping calculated on next step</span>`}
            </div>`
          : nothing}
        ${this.shouldShowPromoCode()
          ? html`
              <hr aria-hidden="true" />
              <mhecomm-promo-code .storeCode=${this.storeCode}></mhecomm-promo-code>
              <hr/>
            `
          : nothing}

        ${this.shouldShowTotalBeforeDiscount() ? 
          html`<div class="d-flex flex-row justify-content-between my-3 fs-5">
          <span>Total Before Discount:</span>
          <span class="fw-bold text-decoration-line-through" data-automation-id="total-before-discount"
            >${getPriceWithCurrency(this.currencyCode, this.totalBeforeDiscount!)}</span
          >
        </div>`: nothing}
        <div class="d-flex flex-row justify-content-between my-3 fs-5">
          <span class="fw-semibold">${this.finalSummary ? html`Total:` : html`${freeTrialProduct ? 'Total Today' : 'Estimated Total'}:`}</span>
          <span class="fw-bold" data-automation-id="estimated-total-value"
            >${getPriceWithCurrency(this.currencyCode, this.total!)}</span
          >
        </div>
      `;
    }
    return this.loadingHtmlTmpl();
  }

  shouldShowPromoCode() {
    if (hasOnlyFreeTrialProducts(getProductsFromCart(store.getState() as StoreState))) {
      return false;
    }

    return (this.isLoggedInUser && !this.b2bStoretype) || isGuestCheckoutPage();
  }

  shouldShowEstimatedTax() {
    return !hasOnlyFreeTrialProducts(getProductsFromCart(store.getState() as StoreState));
  }

  shouldShowTotalBeforeDiscount() {
    const shopCart = getCart(store.getState() as StoreState);
    return hasBundleDiscount(shopCart) || hasPromoCodeDiscount(shopCart);
  }

  loadingHtmlTmpl() {
    return html`
      ${this.status === ActionStatus.idle || this.status === ActionStatus.loading
        ? html`
            <span class="visually-hidden">Loading cart summary</span>
            ${loading({ width: '50%', m: '' })}
            <hr aria-hidden="true" />
            ${loading()} ${loading()} ${loading()}
            <hr aria-hidden="true" />
            ${loading()}
            <hr aria-hidden="true" />
            ${loading()}
          `
        : nothing}
    `;
  }

  headerHtmlTmpl() {
    if (this.checkoutPreview) return nothing;
    return html`<h2 class="fs-4">Summary</h2>`;
  }

  estimateTaxHtmlTmpl() {
    if (this.totalTax == 0 || this.totalTax) {
      return html` <div class="d-flex flex-row justify-content-between my-3" aria-live="polite" aria-atomic="true">
          <span class="d-flex align-items-center">Tax (${this.taxExemptModalHtmlTmpl()}):</span>
          <span class="fw-bold fs-large" id="total-tax-value">
            ${this.isTaxLoading ? smallSpinner() : html`${getPriceWithCurrency(this.currencyCode, this.totalTax)}`}
          </span>
        </div>
        ${isTunnelPurchaseFlow() && isTaxCalculatedOnSchoolAddress()
          ? html`<div>${this.infoTaxCalculateOnSchool()}</div>`
          : nothing}`;
    } else if (!this.finalSummary) {
      return html` <div class="my-3" aria-live="polite" aria-atomic="true">
        <mhecomm-estimated-tax .storeCode="${this.storeCode}"></mhecomm-estimated-tax>
      </div>`;
    } else {
      return nothing;
    }
  }

  bundleDiscountInfo() {
    return html` <div class="d-flex flex-row justify-content-between my-3" aria-live="polite" aria-atomic="true">
         <span>Bundle Discount:</span>
         <span class="fw-bold fs-large" id="cart-discount-bundle-value"
            >- ${getPriceWithCurrency(this.currencyCode, this.bundleDiscount!)}
          </span>
        </div>`;
  }

  infoTaxCalculateOnSchool() {
    return html` <mhecomm-alert
      id="tax-additional-info"
      type="${MessageType.info}"
      ?hideIcon=${true}
      message=${MessageTemplateId.TAX_CALCULATED_ON_SCHOOL_ADDRESS}
      data-automation-id="tax-additional-info"
    >
    </mhecomm-alert>`;
  }

  taxExemptModalHtmlTmpl() {
    const modalContextId = 'tax-exemption-link';
    this.exemptionModal = new Modal({
      modalContext: {
        componentRef: modalContextId,
        componentRefData: addAnalyticsCheckoutFlowMetadata({
          label: 'Tax (exemptions)',
        }),
      },
      header: 'Tax Exempt',
      body: {
        template: html`All online orders initially display tax. As long as your billing address is your school's address
        and you have previously sent your tax information, you will not be charged tax. Your finalized receipt/invoice
        will reflect the exemption.`,
      },
      disableFooter: true,
      a11y: {
        announceBody: true
      }
    });

    this.exemptionModal.addEventListener(`${modalContextId}-modal-close`, () => {
      (this.shadowRoot?.querySelector(".exemption-button") as HTMLInputElement)?.focus();
    });

    return html`
      <button class="btn btn-link p-0 exemption-button" data-bs-toggle="modal" @click=${this.openModal}>
        <span class="visually-hidden">Tax </span>exemptions<span class="visually-hidden"> (opens in a modal)</span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-summary': CartSummary;
  }
}
